import { useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, Check, Mail, MessageCircle, ArrowLeft, Building2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import {
  SERVICES,
  BRAND,
  BRANCHES,
  buildWhatsAppLink,
  buildEmailLink,
} from "../lib/constants";
import { useTranslation } from "../lib/i18n";
import ServiceIcon from "../components/ServiceIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Per-service marketing copy: benefits + an evocative cover image
const SERVICE_DETAIL = {
  flights: {
    cover: "https://images.pexels.com/photos/12820604/pexels-photo-12820604.jpeg",
    intro:
      "From a single domestic leg to a 14-stop global roadshow, our consultants secure the right seat at the right fare — every time.",
    benefits: [
      "Business & First-class fares from 800+ airlines",
      "Lounge access and priority boarding arranged",
      "Multi-city itineraries with sensible layovers",
      "24/7 rebooking when plans change",
    ],
  },
  visa: {
    cover: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1800&q=80",
    intro:
      "Tourist, business and transit visas handled end-to-end. We prepare the paperwork, liaise with embassies and track every approval until it lands.",
    benefits: [
      "Schengen, UK, USA, UAE, China and 80+ destinations",
      "Document checklist and review before submission",
      "Embassy liaison and appointment booking",
      "Live tracking of every application",
    ],
  },
  hotels: {
    cover: "https://images.unsplash.com/photo-1677129667171-92abd8740fa3?w=1800&q=80",
    intro:
      "Five-star chains, signature boutique stays and corporate-rate properties — booked at the best available rate with the upgrades you'd expect.",
    benefits: [
      "Negotiated corporate rates across 1M+ properties",
      "Suite upgrades and late check-out where available",
      "Direct billing for corporate accounts",
      "Loyalty programme points credited to your profile",
    ],
  },
  cars: {
    cover: "https://images.pexels.com/photos/30576168/pexels-photo-30576168.jpeg",
    intro:
      "Chauffeur-driven black cars for executive transfers or self-drive premium fleet across continents — confirmed in minutes.",
    benefits: [
      "Executive chauffeur or self-drive options",
      "Premium fleet partnerships in 180 countries",
      "Airport-to-meeting transfers, multi-city routes",
      "Meet & greet at arrivals, on standby for delays",
    ],
  },
  insurance: {
    cover: "https://images.pexels.com/photos/6050125/pexels-photo-6050125.jpeg",
    intro:
      "Comprehensive policies covering medical evacuation, trip cancellation, baggage and corporate liability — sized to your traveller risk profile.",
    benefits: [
      "Medical & emergency evacuation worldwide",
      "Trip cancellation, delay and baggage cover",
      "Corporate liability and duty-of-care add-ons",
      "Annual multi-trip plans for frequent flyers",
    ],
  },
  corporate: {
    cover: "https://images.unsplash.com/photo-1767694942371-3a9e2d155b04?w=1600&q=80",
    intro:
      "A complete corporate travel programme: policy compliance, expense visibility, dedicated account manager and 24/7 traveller support — all under one roof.",
    benefits: [
      "Dedicated senior account manager",
      "Travel policy management & approval flows",
      "Real-time spend, CO₂ and compliance dashboards",
      "Group, event and conference travel co-ordinated",
    ],
  },
  cruises: {
    cover: "https://images.unsplash.com/photo-1604503245604-930a6f3513ef?w=1600&q=80",
    intro:
      "Ocean and river cruises with the world's leading lines — staterooms, suites and exclusive shore experiences, hand-picked by our consultants.",
    benefits: [
      "Ocean & river cruise inventory worldwide",
      "Suites, balcony staterooms and concierge tiers",
      "Private shore excursions arranged",
      "Pre- and post-cruise hotel bundles",
    ],
  },
  holidays: {
    cover: "https://images.unsplash.com/photo-1758717152007-6a2eb7299409?w=1600&q=80",
    intro:
      "Honeymoons, milestone celebrations, family safaris and wellness retreats — every detail of your leisure escape, quietly orchestrated.",
    benefits: [
      "Bespoke honeymoons and milestone trips",
      "Family safaris and adventure itineraries",
      "Wellness and spa retreats",
      "Private guides, drivers and concierge support",
    ],
  },
};

export default function ServiceDetail() {
  const { serviceId } = useParams();
  const service = useMemo(
    () => SERVICES.find((s) => s.id === serviceId),
    [serviceId]
  );

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", branch: "auto" });
  const [pending, setPending] = useState("");
  const { t } = useTranslation();

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const detail = SERVICE_DETAIL[service.id] || {};

  const resolveBranch = () => {
    if (form.branch === "auto") return BRANCHES[0];
    return BRANCHES.find((b) => b.id === form.branch) || BRANCHES[0];
  };

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const buildMessage = () => {
    const branch = resolveBranch();
    const lines = [
      `Hello Beyond Borders Travel (${branch.city}, ${branch.country}),`,
      ``,
      `I'd like to enquire about your *${service.title}* service.`,
      ``,
      `Name: ${form.name || "—"}`,
      `Email: ${form.email || "—"}`,
      `Phone: ${form.phone || "—"}`,
      ``,
      `Details:`,
      form.message || "—",
      ``,
      `Thank you.`,
    ];
    return lines.join("\n");
  };

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!form.email.trim()) return "Please enter your email.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email.";
    if (!form.message.trim()) return "Please tell us about your enquiry.";
    return null;
  };

  const logInquiry = (channel) => {
    const branch = resolveBranch();
    // Fire-and-forget so the WhatsApp/email window opens snappily even if
    // the backend is slow. Errors are logged but don't block the user.
    axios
      .post(
        `${API}/inquiry`,
        {
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          message: `[${service.title} · via ${channel} · Branch: ${branch.city}, ${branch.country}]\n\n${form.message}`,
          branch: branch.id,
        },
        { timeout: 6000 }
      )
      .catch((err) => {
        console.warn("Inquiry log failed (continuing):", err?.message);
      });
  };

  const handleWhatsApp = () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setPending("whatsapp");
    logInquiry("WhatsApp");
    const branch = resolveBranch();
    const url = buildWhatsAppLink(buildMessage(), branch.whatsappNumber);
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp — your enquiry is ready to send.");
    setTimeout(() => setPending(""), 400);
  };

  const handleEmail = () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setPending("email");
    logInquiry("Email");
    const branch = resolveBranch();
    const subject = `${service.title} enquiry — ${form.name}`;
    // Route email to the branch's inbox
    const url = buildEmailLink(subject, buildMessage(), branch.emails[0]);
    // Use a transient anchor click so mobile browsers without a default mail
    // app gracefully fall back instead of leaving the SPA on a blank tab.
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Opening your email client — your enquiry is ready to send.");
    setTimeout(() => setPending(""), 400);
  };

  return (
    <div data-testid={`service-detail-${service.id}`}>
      <Toaster theme="dark" position="bottom-center" />

      {/* Hero */}
      <section className="relative h-[52vh] min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          {detail.cover && (
            <img src={detail.cover} alt={service.title} className="h-full w-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-[#05070C] via-[#05070C]/80 to-[#05070C]/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05070C]/30 via-transparent to-[#05070C]" />
        </div>

        <div className="relative container-x pb-16 w-full">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <Link
              to="/services"
              data-testid={`service-back-link-${service.id}`}
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-white/55 hover:text-gold transition-colors mb-6"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              All services
            </Link>

            <div className="flex items-center gap-4 mb-6">
              <span className="grid place-items-center h-14 w-14 rounded-full border border-gold/40 text-gold">
                <ServiceIcon name={service.icon} className="h-6 w-6" />
              </span>
              <div className="text-[11px] uppercase tracking-[0.32em] text-gold/85">
                {service.short}
              </div>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05]">
              {service.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="section">
        <div className="container-x grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: about + benefits */}
          <div className="lg:col-span-6">
            <p className="text-lg text-white/75 leading-relaxed">
              {detail.intro || service.description}
            </p>

            <h3 className="font-display text-2xl text-white mt-12 mb-6">What's included</h3>
            <ul className="space-y-4">
              {(detail.benefits || []).map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 text-white/80"
                  data-testid={`service-benefit-${service.id}`}
                >
                  <Check className="h-5 w-5 text-gold mt-0.5 shrink-0" strokeWidth={2} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="gold-rule mt-12 mb-8" />
            <div className="grid grid-cols-2 gap-6 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 mb-2">Reachable</div>
                <a href={`mailto:${BRAND.email}`} className="text-white/85 hover:text-gold transition-colors break-all">{BRAND.email}</a>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 mb-2">Direct line</div>
                {BRAND.phones.map((p) => (
                  <a key={p} href={`tel:${p.replace(/\s+/g, "")}`} className="block text-white/85 hover:text-gold transition-colors">{p}</a>
                ))}
              </div>
            </div>
          </div>

          {/* Right: enquiry form with WhatsApp + Email */}
          <div className="lg:col-span-6">
            <div className="glass-strong p-8 sm:p-10 relative">
              <div className="absolute top-0 left-0 h-px w-24 bg-gold" />
              <h3 className="font-display text-3xl text-white mb-2">
                Enquire about <span className="gold-text">{service.title}</span>
              </h3>
              <p className="text-white/55 text-sm mb-8">
                Fill in your details once — then send via WhatsApp <span className="text-white/30">or</span> Email. Same enquiry, your choice of channel.
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.24em] text-white/55 mb-2 block">
                    Full name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    data-testid={`service-input-name-${service.id}`}
                    className="w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-white placeholder-white/30 transition-colors"
                    placeholder="Jane Banda"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.24em] text-white/55 mb-2 block">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    data-testid={`service-input-email-${service.id}`}
                    className="w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-white placeholder-white/30 transition-colors"
                    placeholder="jane@company.com"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="text-[11px] uppercase tracking-[0.24em] text-white/55 mb-2 block">
                  Phone (optional)
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  data-testid={`service-input-phone-${service.id}`}
                  className="w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-white placeholder-white/30 transition-colors"
                  placeholder="+265 ..."
                />
              </div>

              <div className="mt-6">
                <label className="text-[11px] uppercase tracking-[0.24em] text-white/55 mb-2 block">
                  Tell us about your enquiry
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  data-testid={`service-input-message-${service.id}`}
                  className="w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-white placeholder-white/30 transition-colors resize-none"
                  placeholder={`e.g. Lilongwe → Dubai for 2 travellers, 4–8 March, business class, 5★ hotel near DIFC`}
                />
              </div>

              {/* Branch router */}
              <div className="mt-8">
                <label className="text-[11px] uppercase tracking-[0.24em] text-white/55 mb-3 block">
                  {t("contact.form.branch")}
                </label>
                <div className="grid sm:grid-cols-3 gap-2" role="radiogroup">
                  {[
                    { value: "auto", label: t("contact.form.branchAuto"), flag: null, subtitle: null },
                    ...BRANCHES.map((b) => ({
                      value: b.id,
                      label: b.city,
                      flag: b.flag,
                      subtitle: b.country,
                    })),
                  ].map((opt) => {
                    const active = form.branch === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setForm((f) => ({ ...f, branch: opt.value }))}
                        data-testid={`service-branch-option-${service.id}-${opt.value}`}
                        className={`text-left p-3.5 border transition-all duration-300 ${
                          active
                            ? "border-gold bg-gold/10 text-white"
                            : "border-white/10 bg-transparent text-white/70 hover:border-gold/50 hover:text-white"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {opt.flag ? (
                            <span className="text-base leading-none" aria-hidden="true">{opt.flag}</span>
                          ) : (
                            <Building2 className={`h-3.5 w-3.5 ${active ? "text-gold" : "text-white/45"}`} />
                          )}
                          <span className="text-sm font-medium truncate">{opt.label}</span>
                        </div>
                        {opt.subtitle && (
                          <div className="mt-1 text-[9px] uppercase tracking-[0.22em] text-white/45">
                            {opt.subtitle}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-10 grid sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={handleWhatsApp}
                  disabled={pending !== ""}
                  data-testid={`service-send-whatsapp-${service.id}`}
                  className="btn-gold group disabled:opacity-60"
                >
                  <MessageCircle className="h-4 w-4" />
                  {pending === "whatsapp" ? "Opening..." : "Send via WhatsApp"}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <button
                  type="button"
                  onClick={handleEmail}
                  disabled={pending !== ""}
                  data-testid={`service-send-email-${service.id}`}
                  className="btn-ghost-gold group disabled:opacity-60"
                >
                  <Mail className="h-4 w-4" />
                  {pending === "email" ? "Opening..." : "Send via Email"}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>

              <p className="mt-6 text-[11px] uppercase tracking-[0.2em] text-white/35 text-center">
                Both options route to the same team · Average response &lt; 8 min
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="section bg-[#0B1120]/40 border-t border-white/5">
        <div className="container-x">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <h2 className="font-display text-3xl sm:text-4xl text-white">
              Other <span className="gold-text">services</span>
            </h2>
            <Link to="/services" data-testid="service-detail-view-all" className="btn-ghost-gold">
              View all services
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {SERVICES.filter((s) => s.id !== service.id)
              .slice(0, 4)
              .map((s) => (
                <Link
                  key={s.id}
                  to={`/services/${s.id}`}
                  data-testid={`service-related-${s.id}`}
                  className="group bg-[#05070C] p-7 hover:bg-[#0B1120] transition-colors duration-500"
                >
                  <span className="grid place-items-center h-11 w-11 rounded-full border border-gold/30 text-gold mb-5 group-hover:border-gold transition-colors">
                    <ServiceIcon name={s.icon} className="h-5 w-5" />
                  </span>
                  <h4 className="font-display text-lg text-white">{s.title}</h4>
                  <p className="mt-1.5 text-[11px] uppercase tracking-[0.22em] text-gold/70">{s.short}</p>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}
